import { useCallback, useEffect, useRef } from 'react'
import Logger from '~/services/Logger'
import { showErrorToast } from '~/utils/toast'

const logger = new Logger('FilePrinter')

const cleanUpIframe = (iframe: HTMLIFrameElement | undefined) => {
  if (!iframe) {
    return
  }
  try {
    logger.info(`Cleaning up print iframe`)
    document.body.removeChild(iframe)
  } catch (error) {
    // If the iframe is already removed, we don't need to do anything but log that it happened
    logger.warn(
      `Error removing the previous print iframe: ${JSON.stringify(error)}`,
    )
  }
}

export interface FilePrinterProps {
  fileURL: string
  onPrintShortcut?: () => void
  onPrintError?: () => void
}

/**
 * Adds a window level keyboard shortcut to print the file at the given URL
 * @param fileURL The URL of the file to print
 */
export const useFilePrinter = ({
  fileURL,
  onPrintShortcut,
  onPrintError,
}: FilePrinterProps) => {
  const iframeRef = useRef<HTMLIFrameElement | undefined>(undefined)

  const printFile = useCallback(async () => {
    cleanUpIframe(iframeRef.current)

    try {
      const response = await fetch(fileURL)
      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)

      logger.info(`Fetched file, creating iframe to print`)
      // Create a new iframe element and append it to the body
      const iframe = document.createElement('iframe')
      iframe.style.display = 'none'
      iframe.src = blobUrl
      document.body.appendChild(iframe)

      if (!iframeRef.current) {
        iframeRef.current = iframe
      }

      // Wait for the iframe to load and print its content
      iframe.onload = function () {
        try {
          logger.info(`iframe loaded, starting print`)
          iframe.contentWindow?.print()
        } catch (error) {
          console.warn(
            `Failed to print iframe content: ${JSON.stringify(error)}`,
          )
          onPrintError?.()
        }
        // We don't remove the iframe here because as soon as you do it dismisses the print screen in chrome. It is cleaned up on unmount or
      }
    } catch (error) {
      console.error('Error fetching or printing the file:', error)
      showErrorToast({
        message: 'Failed to print file.',
      })
    }
  }, [fileURL])

  const handlePrintShortcut = useCallback(
    async (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'p') {
        event.preventDefault()

        logger.info(`Print keyboard shortcut triggered for file ${fileURL}`)
        onPrintShortcut?.()
        await printFile()
      }
    },
    [fileURL, printFile, onPrintShortcut],
  )

  // UseEffect for adding/removing keydown event listener
  useEffect(() => {
    // Add event listener for keydown
    document.addEventListener('keydown', handlePrintShortcut)

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handlePrintShortcut)
    }
  }, [handlePrintShortcut])

  // UseEffect for cleaning up any leftover iframes on unmount
  useEffect(() => {
    return () => {
      cleanUpIframe(iframeRef.current)
    }
  }, [])

  return [printFile]
}
